import React, { useState, useRef, useEffect } from 'react';
import PropTypes from 'prop-types';

import './FileErrorIndicator.scss';

const POPOVER_WIDTH = 320;
const VIEWPORT_MARGIN = 8;

/**
 * Decodes HTML entities (e.g. &quot; / &#39;) that Salesforce validation messages can contain.
 * Uses a detached textarea so only entities are decoded - no markup is ever executed.
 * @param {string} str - raw message
 * @return {string} decoded message
 */
const decodeHtmlEntities = (str) => {
  if (typeof document === 'undefined' || typeof str !== 'string') {
    return str;
  }
  const textarea = document.createElement('textarea');
  // eslint-disable-next-line no-unsanitized/property
  textarea.innerHTML = str;
  return textarea.value;
};

const ErrorGlyph = ({ size }) => (
  <svg className='fileErrorIndicator-icon' viewBox='0 0 16 16' width={size} height={size} aria-hidden='true' focusable='false'>
    <circle cx='8' cy='8' r='6.6' fill='none' stroke='#c23934' strokeWidth='1.8' />
    <line x1='3.7' y1='3.7' x2='12.3' y2='12.3' stroke='#c23934' strokeWidth='1.8' strokeLinecap='round' />
  </svg>
);

ErrorGlyph.propTypes = {
  size: PropTypes.number,
};

/**
 * Computes a viewport-fixed position for the popover from the trigger rect, clamped so it never
 * runs off screen and flipped above the trigger when there is not enough room below.
 * @param {DOMRect} rect - trigger bounding rect
 * @return {object} inline style
 */
const getPopoverStyle = (rect) => {
  if (!rect) {
    return { display: 'none' };
  }
  const viewportWidth = window.innerWidth || document.documentElement.clientWidth;
  const viewportHeight = window.innerHeight || document.documentElement.clientHeight;

  const left = Math.max(
      VIEWPORT_MARGIN,
      Math.min(rect.left, viewportWidth - POPOVER_WIDTH - VIEWPORT_MARGIN),
  );

  const style = { position: 'fixed', left: `${left}px`, width: `${POPOVER_WIDTH}px` };

  // Flip above the trigger if it sits in the lower half of the viewport.
  if (rect.bottom > viewportHeight / 2) {
    style.bottom = `${viewportHeight - rect.top + VIEWPORT_MARGIN}px`;
  } else {
    style.top = `${rect.bottom + VIEWPORT_MARGIN}px`;
  }
  return style;
};

/**
 * Inline red error icon shown to the left of a file name when the file violates one or more
 * Salesforce save-time rules. Hovering or focusing the icon reveals a popover listing every
 * violated rule message. Driven entirely by the messages array.
 * @param {object} props
 * @param {string} props.itemId - file id (for stable automation ids)
 * @param {string[]} props.messages - rule violation messages
 * @return {JSX.Element|null}
 */
export const FileErrorIndicator = ({ itemId, messages = [] }) => {
  const [visible, setVisible] = useState(false);
  const [triggerRect, setTriggerRect] = useState(null);
  const hideTimeout = useRef(null);
  const tooltipId = `fileAction-violations-tooltip-${itemId}`;

  // Clear any pending hide timer on unmount to avoid setState-after-unmount warnings.
  useEffect(() => () => {
    if (hideTimeout.current) {
      clearTimeout(hideTimeout.current);
    }
  }, []);

  if (!messages.length) {
    return null;
  }

  const show = (event) => {
    if (hideTimeout.current) {
      clearTimeout(hideTimeout.current);
    }
    if (event?.currentTarget?.getBoundingClientRect) {
      setTriggerRect(event.currentTarget.getBoundingClientRect());
    }
    setVisible(true);
  };

  // Grace period so the cursor can travel from the icon onto the popover without it closing.
  const hide = () => {
    hideTimeout.current = setTimeout(() => setVisible(false), 100);
  };

  const keepOpen = () => {
    if (hideTimeout.current) {
      clearTimeout(hideTimeout.current);
    }
  };

  return (
    <div className='fileErrorIndicator'>
      <button
        type='button'
        className='fileErrorIndicator-trigger'
        aria-label='File rule violations'
        aria-expanded={visible}
        aria-describedby={visible ? tooltipId : undefined}
        data-testid={`fileAction-violations-icon-${itemId}`}
        onMouseEnter={show}
        onMouseLeave={hide}
        onFocus={show}
        onBlur={hide}
      >
        <ErrorGlyph size={16} />
      </button>
      {visible &&
        <div
          className='error-popover'
          id={tooltipId}
          role='tooltip'
          aria-label='Errors'
          data-testid={`fileAction-violations-popover-${itemId}`}
          style={getPopoverStyle(triggerRect)}
          onMouseEnter={keepOpen}
          onMouseLeave={hide}
        >
          <header className='error-popover-header'>
            <span className='error-popover-headerIcon'>
              <ErrorGlyph size={18} />
            </span>
            <h2 className='error-popover-title'>Errors</h2>
          </header>
          <div className='error-popover-body'>
            <ul className='error-popover-list'>
              {messages.map((message, index) => (
                <li key={`${index}-${message}`}>{decodeHtmlEntities(message)}</li>
              ))}
            </ul>
          </div>
        </div>
      }
    </div>
  );
};

FileErrorIndicator.propTypes = {
  itemId: PropTypes.string.isRequired,
  messages: PropTypes.arrayOf(PropTypes.string),
};

export default FileErrorIndicator;
